import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { get, post } from '../utils/apiClient';
import { useConversationSession, requestAiResponse, isServerError } from '../hooks/useConversationSession';
import {
  MESSAGES_BOOTSTRAP_QUERY_KEY,
  useMessagesBootstrap,
} from '../hooks/useMessagesBootstrap';
import { logger } from "../utils/logger";
const uuidv4 = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
import { setSessionId, clearSessionId } from '../utils/sessionUtils';
import { toast } from 'react-toastify';
import { AuthContext } from './AuthContext';

import { nowISO } from './../utils/dateUtils';

export interface ConversationMessage {
  id?: string;
  role?: string;
  text?: string;
  sender?: string;
  noteId?: string;
  timestamp?: string;
  options?: unknown[];
  template_options?: unknown[];
  note_type_options?: unknown[];
  encounter_options?: unknown[];
  default_template_id?: string;
  suggested_action?: string;
  [key: string]: unknown;
}

/** Display-format message used by TherapistPanel / useMessageLoading. */
export interface DisplayMessage {
  sender: 'user' | 'assistant';
  text: string;
  suggested_action?: string;
  options?: string[];
  template_options?: unknown[];
}

export interface Conversation {
  id?: string;
  session_id?: string;
  title?: string;
  created_at?: string;
  updated_at?: string;
  message_count?: number;
  [key: string]: unknown;
}

export interface ConversationContextValue {
  conversations: Conversation[];
  currentSessionId: string | null;
  messages: ConversationMessage[];
  isLoading: boolean;
  isProcessing: boolean;
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>;
  error: string | null;
  scrollPosition: number;
  fetchRecentConversations: () => Promise<void>;
  fetchAllConversations: (searchTerm?: string) => Promise<Conversation[]>;
  startNewConversation: () => Promise<string | null>;
  updateConversationTitle: (sessionId: string, newTitle: string) => Promise<boolean>;
  switchToConversation: (sessionId: string) => Promise<boolean>;
  updateScrollPosition: (position: number) => void;
  fetchConversationMessages: (sessionId: string, force?: boolean) => Promise<ConversationMessage[]>;
  processInput: (textContent: string, notePrefs?: unknown, contextData?: unknown) => Promise<unknown>;
  sendMessage: (textContent: string, notePrefs?: unknown, contextData?: unknown) => Promise<unknown>;
  cancelProcessing: () => void;
  hasFetched: React.MutableRefObject<boolean>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  rehydrating: boolean;
  sessionLoading: boolean;
  /** Cache display-format messages so they survive TherapistPanel unmount/remount (tab switch). */
  cacheDisplayMessages: (sessionId: string | null | undefined, msgs: DisplayMessage[]) => void;
  /** Read the cached display-format messages for a given session (returns [] if nothing cached or session mismatch). */
  getCachedDisplayMessages: (sessionId: string | null | undefined) => DisplayMessage[];
  /**
   * Guided-session state machine — persists across TherapistPanel unmount/remount
   * (tab switches) AND page reloads (backed by sessionStorage).
   *
   * guidedSessionInfo tracks which session was created for which guided mode.
   * guidedPromptSentForSession tracks whether the AI prompt was already sent.
   */
  getGuidedSessionInfo: () => { sessionId: string; mode: string } | null;
  setGuidedSessionInfo: (sessionId: string, mode: string) => void;
  clearGuidedSessionInfo: () => void;
  getGuidedPromptSentForSession: () => string | null;
  setGuidedPromptSentForSession: (sessionId: string) => void;
  clearGuidedPromptSentForSession: () => void;
  /** Patient detected by the AI from conversation state (auto-populated). */
  aiCurrentPatient: { patient_id: string; patient_name: string } | null;
  setAiCurrentPatient: React.Dispatch<React.SetStateAction<{ patient_id: string; patient_name: string } | null>>;
}

const ConversationContext = createContext<ConversationContextValue | null>(null);

export { ConversationContext };

export function ConversationProvider({ children, userId }: { children: React.ReactNode; userId?: string | null }) {
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [error, setError] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [aiCurrentPatient, setAiCurrentPatient] = useState<{ patient_id: string; patient_name: string } | null>(null);
  const auth = useContext(AuthContext);

  
  const {
    sessionId: currentSessionId,
    messages,
    loading: sessionLoading,
    rehydrating,
    createNewSession,
    fetchMessages
  } = useConversationSession();
  
  const hasFetched = useRef(false);

  // Display-format message cache — survives TherapistPanel unmount/remount
  // because it lives on the persistent ConversationProvider.  Updated by
  // useMessageLoading whenever local messages change; read by the lazy
  // initialiser on remount so the first render already shows the conversation.
  const displayMessagesCacheRef = useRef<{ sessionId: string | null | undefined; messages: DisplayMessage[] }>({ sessionId: null, messages: [] });
  const cacheDisplayMessages = useCallback((sessionId: string | null | undefined, msgs: DisplayMessage[]) => {
    displayMessagesCacheRef.current = { sessionId, messages: msgs };
  }, []);
  const getCachedDisplayMessages = useCallback((sessionId: string | null | undefined): DisplayMessage[] => {
    if (displayMessagesCacheRef.current.sessionId === sessionId && sessionId != null) {
      return displayMessagesCacheRef.current.messages;
    }
    return [];
  }, []);

  // ── Guided-session state machine ──────────────────────────────────────
  // Lives on ConversationProvider (never unmounts) AND backed by
  // sessionStorage so it survives page reloads too.
  // This is the root-cause fix: TherapistPanel refs reset on every tab
  // switch, but these persist, letting useSessionManagement correctly
  // distinguish "resume guided session" from "create new guided session".
  const GUIDED_SESSION_KEY = 'guidedSessionInfo';
  const GUIDED_PROMPT_KEY = 'guidedPromptSentForSession';

  // Read initial value from sessionStorage (React 18 doesn't support lazy useRef initialisers)
  let initialGuidedSessionInfo: { sessionId: string; mode: string } | null = null;
  try {
    const raw = sessionStorage.getItem(GUIDED_SESSION_KEY);
    if (raw) initialGuidedSessionInfo = JSON.parse(raw);
  } catch { /* corrupt data — start fresh */ }
  const guidedSessionInfoRef = useRef<{ sessionId: string; mode: string } | null>(initialGuidedSessionInfo);
  const guidedPromptSentRef = useRef<string | null>(
    sessionStorage.getItem(GUIDED_PROMPT_KEY)
  );

  const getGuidedSessionInfo = useCallback((): { sessionId: string; mode: string } | null => {
    return guidedSessionInfoRef.current;
  }, []);
  const setGuidedSessionInfo = useCallback((sessionId: string, mode: string) => {
    const info = { sessionId, mode };
    guidedSessionInfoRef.current = info;
    try { sessionStorage.setItem(GUIDED_SESSION_KEY, JSON.stringify(info)); } catch { /* quota */ }
  }, []);
  const clearGuidedSessionInfo = useCallback(() => {
    guidedSessionInfoRef.current = null;
    try { sessionStorage.removeItem(GUIDED_SESSION_KEY); } catch { /* ignore */ }
  }, []);

  const getGuidedPromptSentForSession = useCallback((): string | null => {
    return guidedPromptSentRef.current;
  }, []);
  const setGuidedPromptSentForSession = useCallback((sessionId: string) => {
    guidedPromptSentRef.current = sessionId;
    try { sessionStorage.setItem(GUIDED_PROMPT_KEY, sessionId); } catch { /* quota */ }
  }, []);
  const clearGuidedPromptSentForSession = useCallback(() => {
    guidedPromptSentRef.current = null;
    try { sessionStorage.removeItem(GUIDED_PROMPT_KEY); } catch { /* ignore */ }
  }, []);
  
  useEffect(() => {
    if (sessionLoading) {
      setIsLoading(true);
    }
  }, [sessionLoading]);
  
  // Recent conversations are fetched as one section of the shared messages
  // bootstrap bundle (`/api/messages/bootstrap`) so Layout, MessagesCenter,
  // and this provider all dedupe into a single network call on cold load.
  const queryClient = useQueryClient();
  const { data: messagesBootstrap } = useMessagesBootstrap();
  const bootstrapRecent = messagesBootstrap?.recent_conversations;

  useEffect(() => {
    if (bootstrapRecent) {
      setConversations(bootstrapRecent);
    }
  }, [bootstrapRecent]);

  const fetchRecentConversations = useCallback(async () => {
    if (!userId) return;
    // Trigger a refetch of the shared bootstrap so the recent-conversations
    // section is re-read.  Observing effect above picks up the new data.
    await queryClient.invalidateQueries({ queryKey: MESSAGES_BOOTSTRAP_QUERY_KEY });
  }, [userId, queryClient]);
  
  const fetchAllConversations = async (searchTerm = '') => {
    if (!userId) return [];
    
    setIsLoading(true);
    try {
      const endpoint = searchTerm 
        ? `/api/conversations/all/${userId}?search=${encodeURIComponent(searchTerm)}`
        : `/api/conversations/all/${userId}`;
        
      const response = await get(endpoint);
      
      if (response.ok) {
        const data = await response.json();
        return data;
      }
      return [];
    } catch (error) {
      logger.error('Error fetching all conversations:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };
  
  const startNewConversation = useCallback(async () => {
    if (sessionLoading || rehydrating) {
      logger.log("[SESSION_REHYDRATE_DEBUG] block_create_while_bootstrapping sessionLoading=", sessionLoading, " rehydrating=", rehydrating);
      return null;
    }
    if (!userId) {
      logger.warn("⚠️ No userId found. Waiting for auth to complete...");
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (!userId) {
        logger.error("❌ Still no userId after waiting. Cannot start conversation.");
        return null;
      }
    }

    logger.log("📣 startNewConversation called with userId:", userId);
    logger.log("🔍 User at conversation start:", { userId });
    
    setIsLoading(true);
    try {
      if (currentSessionId) {
        logger.log("🔄 Finalizing existing session before creating new one:", currentSessionId);
        post(`/api/engine/finalize-session`, {
          session_id: currentSessionId
        }, { timeout: 120000 }).catch((e) => logger.warn("Finalize-session non-blocking error:", e?.status || e));
      }
      
      const newSessionId = await createNewSession();

      // Clear stale patient context from the previous session
      setAiCurrentPatient(null);
      
      // Optimistic update: add new conversation to local state immediately
      // so the sidebar shows it before the backend fetch completes
      if (newSessionId) {
        const now = nowISO();
        setConversations(prev => [
          { id: newSessionId, title: 'New Conversation', created_at: now, last_updated: now },
          ...prev
        ]);
      }
      
      fetchRecentConversations();
      
      return newSessionId;
    } catch (error) {
      logger.error('❌ Error creating new conversation:', error);
      toast.error("Failed to start session. Please try again.");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [userId, currentSessionId, createNewSession, sessionLoading, rehydrating]);

  
  const updateConversationTitle = async (sessionId, newTitle) => {
    if (!userId || !sessionId) return false;
    
    try {
      const response = await post(`/api/conversations/${sessionId}/update`, {
        title: newTitle
      });
      
      setConversations(prev => 
        prev.map(conv => 
          conv.id === sessionId 
            ? { ...conv, title: newTitle, last_updated: nowISO() } 
            : conv
        )
      );
      return true;
    } catch (error) {
      logger.error('Error updating conversation title:', error);
      return false;
    }
  };
  
  const switchToConversation = async (sessionId) => {
    if (!userId || !sessionId) return false;
    
    setIsLoading(true);
    try {
      if (currentSessionId && currentSessionId !== sessionId) {
        post(`/api/engine/finalize-session`, {
          session_id: currentSessionId
        }, { timeout: 120000 }).catch((e) => logger.warn("Finalize-session non-blocking error:", e?.status || e));
      }
      
      setSessionId(sessionId);

      // Clear stale patient context when switching sessions
      setAiCurrentPatient(null);
      
      setConversations(prev => 
        prev.map(conv => 
          conv.id === sessionId 
            ? { ...conv, last_updated: nowISO() } 
            : conv
        )
      );
      
      return true;
    } catch (error) {
      logger.error('Error switching conversation:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  const updateScrollPosition = (position) => {
    setScrollPosition(position);
  };
  
  const handleSessionFailure = useCallback((error, sessionId) => {
    logger.error('Session failure:', error);
    
    if (error && (error.includes('session not found') || error.includes('invalid session'))) {
      logger.warn("🗑️ Clearing invalid session from storage");
      clearSessionId();
    }
    
    return { messages: [], total: 0, page: 1, totalPages: 0, error: error };
  }, []);

  // Returns fetched messages directly (not stale closure state) to avoid
  // callers seeing an empty array from the previous render's `messages`.
  // Pass force=true to bypass the hasFetchedMessagesRef guard (e.g. on tab-switch remount).
  const fetchConversationMessages = useCallback(async (sessionId, force = false) => {
    const fetched = await fetchMessages(force);
    return fetched;
  }, [fetchMessages]);
  
  const processInput = useCallback(async (textContent, notePrefs = null, contextData = null) => {
    if (!userId) {
      logger.warn("⚠️ Cannot process input: missing userId");
      return null;
    }

    let sid = currentSessionId;

    if (!sid) {
      logger.warn("⚠️ No session ID - attempting to create new session");
      try {
        const newSessionId = await createNewSession();
        if (!newSessionId) {
          logger.error("❌ Failed to create session for message processing");
          return null;
        }
        setSessionId(newSessionId);
        sid = newSessionId;
        await new Promise(resolve => setTimeout(resolve, 50));
      } catch (error) {
        const msg = String(error?.message || error);
        logger.error("❌ Session creation failed during message processing:", msg);
        if (/Not authenticated/i.test(msg)) {
          try {
            await post('/api/auth/demo-login', { character_type: 'therapist' });
            await new Promise(r => setTimeout(r, 200));
            try {
              const retryId = await createNewSession();
              if (retryId) {
                setSessionId(retryId);
                sid = retryId;
                await new Promise(r => setTimeout(r, 50));
              } else {
                try { toast.info("Please use Demo Login before starting a conversation."); } catch (err) { if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') logger.error('ConversationContext toast error:', err); }
                return null;
              }
            } catch (err) { if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') logger.error('ConversationContext:', err);
              try { toast.info("Please use Demo Login before starting a conversation."); } catch (err2) { if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') logger.error('ConversationContext toast error:', err2); }
              return null;
            }
          } catch (err) { if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') logger.error('ConversationContext:', err);
            try { toast.info("Please use Demo Login before starting a conversation."); } catch (err3) { if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') logger.error('ConversationContext toast error:', err3); }
            return null;
          }
        } else {
          return null;
        }
      }
    }

    logger.log("📤 [processInput called] Input was:", textContent);
    logger.log("🧪 [processInput] sessionId used:", sid);

    const messageId = uuidv4();
    logger.log("🆔 Generated message ID:", messageId);

    try {
      logger.log(`📬 Fetching URL: /api/engine/respond`);
      const derivedAssistant =
        'therapist';

      const bodyPayload: Record<string, unknown> = {
        user_input: textContent,
        assistant_type: derivedAssistant,
        session_id: sid,
        message_id: messageId
      };

      if (notePrefs) {
        bodyPayload.note_prefs = notePrefs;
      }

      if (contextData) {
        bodyPayload.ai_context = contextData;
      }

      try {
        logger.log("📦 Body:", JSON.stringify(bodyPayload));
      } catch (stringifyError) {
        logger.log("📦 Body (with circular refs):", {
          user_input: typeof bodyPayload.user_input === 'string' ? bodyPayload.user_input.substring(0, 100) : '',
          assistant_type: bodyPayload.assistant_type,
          session_id: bodyPayload.session_id,
          message_id: bodyPayload.message_id,
          has_note_prefs: !!bodyPayload.note_prefs,
          has_ai_context: !!bodyPayload.ai_context
        });
      }

      let data;
      try {
        data = await requestAiResponse(bodyPayload, {
          signal: abortControllerRef.current?.signal,
          jitter: true,
          onRetryScheduled: (nextAttempt, totalAttempts, waitMs) => {
            toast.info(`AI is retrying... (attempt ${nextAttempt} of ${totalAttempts})`, {
              toastId: 'ai-retry',
              autoClose: waitMs + 500,
            });
          },
          onRetrySucceeded: () => {
            toast.dismiss('ai-retry');
          },
        });
      } catch (e) {
        // Immediately exit on user cancellation — don't retry, no toast
        if ((e as Error).name === 'AbortError') {
          logger.log('🚫 Request cancelled by user');
          return null;
        }
        // All retries exhausted or non-retryable error
        if (isServerError(e) || (e as { status?: number }).status === 429) {
          const errData = (e as { data?: { error?: { message?: string } } })?.data;
          const retryAfter = (e as { retryAfter?: number }).retryAfter;
          const msg = (e as { status?: number }).status === 429
            ? retryAfter
              ? `AI is temporarily rate-limited. Please try again in ${retryAfter} seconds.`
              : "AI is temporarily rate-limited. Please try again in a moment."
            : errData?.error?.message || "AI temporarily unreachable. Please try again in a moment.";
          toast.dismiss('ai-retry');
          toast.error(msg, { autoClose: 15000, toastId: 'ai-final-error' });
          logger.error("AI unreachable after retries:", errData || (e as { message?: string })?.message);
          throw e;
        }
        toast.dismiss('ai-retry');
        throw e;
      }

      logger.log("📥 API Response:", data);

      // Surface current_patient from backend state to the Context Panel
      if (data?.current_patient?.patient_id) {
        setAiCurrentPatient({
          patient_id: data.current_patient.patient_id,
          patient_name: data.current_patient.patient_name || '',
        });
      }

      if (data && (data.error_code || data.error || data.message && data.status === 'error')) {
        const msg = data?.message || data?.error?.message || 'Assistant error';
        const code = data?.error_code || data?.error?.code || '';
        try { toast.error(code ? `${msg} (${code})` : msg); } catch (_) { /* ignore */ }
        return data;
      }

      if (data && data.status === "degraded") {
        logger.warn("AI degraded:", data);
        try { toast.warn(data.message || "AI temporarily unavailable"); } catch (err) { if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') logger.error('ConversationContext toast error:', err); }
        return data;
      }

      if (data.action === "create_session") {
        logger.log("🔧 Backend suggests creating new session");
        await createNewSession();
        return null;
      }

      if (data.already_processed) {
        logger.warn("🔄 Message was already processed on the server side");
        return null;
      }

      if (data.session_id && data.session_id !== sid) {
        logger.log("🔧 Backend auto-created session:", data.session_id);
        setSessionId(data.session_id);
      }

      return data;
    } catch (error) {
      // Silently exit on user cancellation
      if ((error as Error).name === 'AbortError') {
        return null;
      }
      logger.error("❌ Error processing input:", error);
      // No toast here — the retry loop (lines 409/421) already shows an appropriate toast.
      // We only re-throw so useMessageActions can mark the message as 'failed'.
      throw error;
    }
  }, [userId, currentSessionId, createNewSession]);

  const cancelProcessing = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsProcessing(false);
    logger.log('🚫 User cancelled AI processing');
  }, []);

  const sendMessage = useCallback(async (textContent, notePrefs = null, contextData = null) => {
    logger.log("📤 [ConversationContext sendMessage] Input was:", textContent);
    const myController = new AbortController();
    abortControllerRef.current = myController;
    setIsProcessing(true);
    setError(null);
    // Keep the send spinner active when the backend starts a durable note
    // generation job; the recovery hook / completion event will clear it.
    let keepProcessing = false;
    try {
      const result = await processInput(textContent, notePrefs, contextData);
      if (result?.request_id && result?.suggested_action === 'generating_note') {
        keepProcessing = true;
      }
      // After a successful AI response, refresh conversation list after a
      // short delay so the auto-generated title from the backend has time to
      // be written to the DB before we fetch.
      if (result && userId) {
        setTimeout(() => {
          fetchRecentConversations();
        }, 3000);
      }
      return result;
    } catch (error) {
      // Ignore AbortError from user cancellation
      if ((error as Error).name === 'AbortError') {
        logger.log('🚫 Request aborted by user');
        return null;
      }
      logger.error("❌ Assistant error:", error);
      // No setError() here — useMessageActions will handle error state via the catch block.
      // This avoids a duplicate error banner alongside the per-message "Failed to send" indicator.
      throw error;
    } finally {
      // Only reset isProcessing if no newer request has started and this
      // response did not hand off to the durable generation recovery hook.
      if (!keepProcessing && abortControllerRef.current === myController) {
        setIsProcessing(false);
      }
    }
  }, [processInput, userId, cancelProcessing]);

  
  return (
    <ConversationContext.Provider
      value={{
        conversations,
        currentSessionId,
        messages,
        isLoading,
        isProcessing,
        setIsProcessing,
        error,
        scrollPosition,
        fetchRecentConversations,
        fetchAllConversations,
        startNewConversation,
        updateConversationTitle,
        switchToConversation,
        updateScrollPosition,
        fetchConversationMessages,
        processInput,
        sendMessage,
        cancelProcessing,
        hasFetched,
        setError,
        rehydrating,
        sessionLoading,
        cacheDisplayMessages,
        getCachedDisplayMessages,
        getGuidedSessionInfo,
        setGuidedSessionInfo,
        clearGuidedSessionInfo,
        getGuidedPromptSentForSession,
        setGuidedPromptSentForSession,
        clearGuidedPromptSentForSession,
        aiCurrentPatient,
        setAiCurrentPatient
      }}
    >
      {children}
    </ConversationContext.Provider>
  );
}

export function useConversation(): ConversationContextValue {
  try {
    const context = useContext(ConversationContext);
    return context ?? {
      conversations: [],
      currentSessionId: null,
      messages: [],
      isLoading: false,
      isProcessing: false,
      setIsProcessing: () => {},
      error: null,
      scrollPosition: 0,
      fetchRecentConversations: async () => {},
      fetchAllConversations: async () => [],
      startNewConversation: async () => null,
      updateConversationTitle: async () => false,
      switchToConversation: async () => false,
      updateScrollPosition: () => {},
      fetchConversationMessages: async () => [],
      processInput: async () => null,
      sendMessage: async () => null,
      cancelProcessing: () => {},
      hasFetched: { current: false },
      setError: () => {},
      rehydrating: false,
      sessionLoading: false,
      cacheDisplayMessages: () => {},
      getCachedDisplayMessages: () => [],
      getGuidedSessionInfo: () => null,
      setGuidedSessionInfo: () => {},
      clearGuidedSessionInfo: () => {},
      getGuidedPromptSentForSession: () => null,
      setGuidedPromptSentForSession: () => {},
      clearGuidedPromptSentForSession: () => {},
      aiCurrentPatient: null,
      setAiCurrentPatient: () => {},
    };
  } catch (error) {
    logger.warn('useConversation: ConversationContext not available during render:', error);
    return {
      conversations: [],
      currentSessionId: null,
      messages: [],
      isLoading: false,
      isProcessing: false,
      setIsProcessing: () => {},
      error: null,
      scrollPosition: 0,
      fetchRecentConversations: async () => {},
      fetchAllConversations: async () => [],
      startNewConversation: async () => null,
      updateConversationTitle: async () => false,
      switchToConversation: async () => false,
      updateScrollPosition: () => {},
      fetchConversationMessages: async () => [],
      processInput: async () => null,
      sendMessage: async () => null,
      cancelProcessing: () => {},
      hasFetched: { current: false },
      setError: () => {},
      rehydrating: false,
      sessionLoading: false,
      cacheDisplayMessages: () => {},
      getCachedDisplayMessages: () => [],
      getGuidedSessionInfo: () => null,
      setGuidedSessionInfo: () => {},
      clearGuidedSessionInfo: () => {},
      getGuidedPromptSentForSession: () => null,
      setGuidedPromptSentForSession: () => {},
      clearGuidedPromptSentForSession: () => {},
      aiCurrentPatient: null,
      setAiCurrentPatient: () => {},
    };
  }
}
